import React, { useState, useEffect } from "react";
import { getAPIURL, getVendorID, getVendorSettings } from "../../services/helper";
import http from "../../services/http";
import {
  Spinner,
  Container,
  Card,
  Input,
  Button,
  Row,
  Col,
  CardBody,
  CardTitle,
} from "reactstrap";
import {
  FaMoneyBillWave,
  FaChartLine,
  FaFileInvoiceDollar,
  FaDollarSign,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { openView } from "../../ReduxToolkit/Reducers/viewReducer";

const SalesTaxReport = ({ backTo }) => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0], // Last month
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchTaxReport();
  }, []);

  const printReport = () => {
    const vendor_email = getVendorSettings("email");
    const vendor_store_name = getVendorSettings("name");
    const vendor_address = getVendorSettings("vendor_contact_address");
    const vendor_phone = getVendorSettings("vendor_contact_phone");
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Sales Tax Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h2 { margin: 0; }
            .header p { margin: 0; }
            .header h3 { margin-top: 20px; margin-bottom: 10px; }
            .header hr { margin-top: 10px; }
            .summary { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${vendor_store_name}</h2>
            <p>${vendor_address}</p>
            <p>${vendor_phone} | ${vendor_email}</p>
            <h3>Sales Tax Report</h3>
            <hr>
          </div>
          <div class="summary">
            <p><strong>Date Range:</strong> ${dateRange.start} to ${dateRange.end}</p>
            <p><strong>Total Tax Collected:</strong> $${(data?.total_tax_collected || 0).toFixed(2)}</p>
            <p><strong>Gross Sales:</strong> $${(data?.gross_sales || 0).toFixed(2)}</p>
            <p><strong>Net Sales:</strong> $${(data?.net_sales || 0).toFixed(2)}</p>
            <p><strong>Total Transactions:</strong> ${data?.transaction_count || 0}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const fetchTaxReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ vendor_id: vendorId });
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);

      const { data } = await http.get(
        `${api_url}/reports/sales/tax?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching sales tax report:", error);
    }
    setLoading(false);
  };

  return (
    <div className="page-body">
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          {backTo && (
            <Button color="primary" onClick={() => dispatch(openView(backTo))}>
              Back
            </Button>
          )}
          <h2 className="text-center flex-grow-1">Sales Tax Report</h2>
          <div></div>
        </div>

        {/* Filters */}
        <div className="d-flex mb-3 gap-2">
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) =>
              setDateRange({ ...dateRange, start: e.target.value })
            }
          />
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) =>
              setDateRange({ ...dateRange, end: e.target.value })
            }
          />
          <Button color="primary" onClick={fetchTaxReport}>
            Filter
          </Button>
          <Button
            color="info"
            onClick={() => {
              setDateRange({
                start: new Date(new Date().setMonth(new Date().getMonth() - 1))
                  .toISOString()
                  .split("T")[0],
                end: new Date().toISOString().split("T")[0],
              });
              fetchTaxReport();
            }}
          >
            Reset
          </Button>
        </div>

        {/* Summary Widgets */}
        {loading ? (
          <div className="text-center">
            <Spinner color="primary" />
            <p>Loading Sales Tax Report...</p>
          </div>
        ) : (
          <Row className="text-center my-4">
            {[
              {
                icon: (
                  <FaFileInvoiceDollar
                    size={40}
                    className="text-success mb-2"
                  />
                ),
                label: "Total Tax Collected",
                value: `$${(data?.total_tax_collected || 0).toFixed(2)}`,
              },
              {
                icon: <FaChartLine size={40} className="text-info mb-2" />,
                label: "Gross Sales",
                value: `$${(data?.gross_sales || 0).toFixed(2)}`,
              },
              {
                icon: <FaDollarSign size={40} className="text-primary mb-2" />,
                label: "Net Sales",
                value: `$${(data?.net_sales || 0).toFixed(2)}`,
              },
              {
                icon: (
                  <FaMoneyBillWave size={40} className="text-warning mb-2" />
                ),
                label: "Total Transactions",
                value: data?.transaction_count || 0,
              },
            ].map((item, index) => (
              <Col md={4} key={index}>
                <Card>
                  <CardBody>
                    {item.icon}
                    <CardTitle tag="h5">{item.label}</CardTitle>
                    <h4>{item.value}</h4>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        
        <div className="text-center mt-4 mb-4">
          <Button color="success" onClick={printReport}>
            Print Report
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default SalesTaxReport;
